import { Button, Form, Input, Space } from "antd";
import { useEffect } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import FileIcon from "../../../assets/icons/FileIcon";
import {
  useGetEmployeeDetailsQuery,
  useUpdateEmployeeAdditionalDataMutation,
} from "../../../features/employee/employeeApi";
import {
  useGetAdditionalDataQuery,
  useGetEmployeeAdditionalFieldsQuery,
} from "../../../features/employeeAdditionalField/employeeAdditionalFieldApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { formatExtensions } from "../../../utils/utils";
import InfoTitle from "../../common/InfoTitle";
import Loader from "../../ui/Loader";
import EmployeeFileUpload from "../EmployeeFileUpload";
import { replaceEditWithView } from "./utils";

export default function AdditionalInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { editId } = useParams();
  let [queryParams] = useSearchParams();
  const section_id = Number(queryParams.get("type"));
  const { data: additionalSectionData } = useGetAdditionalDataQuery({});

  const {
    isLoading: isAdditionalLoading,
    isError: isAdditionalError,
    error: additionalError,
    data: additionalData,
  } = useGetEmployeeAdditionalFieldsQuery(
    {
      params: { skip: 0, limit: 0, section_id },
    },
    {
      skip: isNaN(section_id),
    }
  );
  const {
    isLoading: isGetEmployeeLoading,
    isError: isGetEmployeeDetailsError,
    error: getEmployeeDetailsError,
    data: getEmployeeData,
  } = useGetEmployeeDetailsQuery(editId || "", {
    skip: !editId,
  });
  const [
    updateEmployeeAdditionalData,
    { isLoading, isError, error, isSuccess },
  ] = useUpdateEmployeeAdditionalDataMutation();
  useError(
    isGetEmployeeDetailsError,
    getEmployeeDetailsError,
    "Get employee data fail"
  );
  useError(
    isAdditionalError,
    additionalError,
    "Get additional information fail"
  );
  useError(isError, error, "Update additional information fail");

  const findData = additionalSectionData?.data?.additionalSections?.find(
    (item) => item?.id === section_id
  );

  const onFinish = (values: { [key: string]: any }) => {
    if (editId) {
      const newData = {
        business_member_id: Number(editId),
        additional_data: additionalData?.data?.additionalFields
          ?.filter((el) => values?.[el?.name])
          ?.map((item) => {
            const fileAdditionalData =
              getEmployeeData?.data?.employee?.additional_data?.find(
                (el) => el?.field_id === item?.id
              );

            return {
              additional_data_id: fileAdditionalData?.id || 0,
              field_id: item?.id,
              value: values?.[item?.name],
            };
          }),
      };

      updateEmployeeAdditionalData({
        body: newData,
        id: editId,
      });
    }
  };

  const findValue = additionalData?.data?.additionalFields?.filter((el) =>
    getEmployeeData?.data?.employee?.additional_data?.some(
      (item) =>
        el?.id === item?.field_id &&
        el?.section_id === item?.additional_field?.section?.id
    )
  );

  const othersValue = additionalData?.data?.additionalFields?.filter(
    (el) => !findValue?.some((item) => item?.id === el?.id)
  );

  useEffect(() => {
    if (findValue?.length) {
      const employeeDataField = findValue?.map((item) => {
        const findData = getEmployeeData?.data?.employee?.additional_data?.find(
          (el) => el?.field_id === item?.id
        );

        return {
          ...item,
          value: findData?.value,
        };
      });

      form.setFieldsValue({
        ...Object.fromEntries(
          employeeDataField?.map((item) => [item?.name, item?.value])
        ),
      });
    }
  }, [findValue, form, getEmployeeData?.data?.employee?.additional_data]);

  const backLink = replaceEditWithView(location.pathname + location.search);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", `${findData?.label} updated successfully!`);
      navigate(replaceEditWithView(backLink));
    }
  }, [findData?.label, isSuccess, backLink, navigate]);

  return (
    <div>
      <InfoTitle className="mb-6" icon={<FileIcon />}>
        {findData?.label}
      </InfoTitle>
      {isGetEmployeeLoading || isAdditionalLoading ? (
        <Loader />
      ) : findValue?.length || othersValue?.length ? (
        <Form
          className="custom-form"
          layout="vertical"
          requiredMark="optional"
          size="large"
          onFinish={onFinish}
          form={form}
        >
          {findValue?.map((item) => (
            <Form.Item
              key={item.name}
              name={item?.name}
              label={item?.label}
              className="mb-4"
            >
              {item?.type === "string" ? (
                <Input size="large" placeholder="Ex: Some value" />
              ) : item?.type === "file" ? (
                <EmployeeFileUpload
                  form={form}
                  accept={formatExtensions(item?.rules?.extensions)}
                  name={item?.name}
                  onChange={(url) =>
                    form.setFieldsValue({
                      [item?.name]: url,
                    })
                  }
                />
              ) : null}
            </Form.Item>
          ))}

          {othersValue?.map((item) => (
            <Form.Item
              key={item.name}
              name={item?.name}
              label={item?.label}
              className="mb-4"
            >
              {item?.type === "string" ? (
                <Input size="large" placeholder="Ex: Some value" />
              ) : item?.type === "file" ? (
                <EmployeeFileUpload
                  form={form}
                  accept={formatExtensions(item?.rules?.extensions)}
                  name={item?.name}
                  onChange={(url) =>
                    form.setFieldsValue({
                      [item?.name]: url,
                    })
                  }
                />
              ) : null}
            </Form.Item>
          ))}

          <Space className="w-full mt-2" size={12}>
            <Button
              className="normal-dark-button modal-button"
              onClick={() => navigate(backLink)}
              size="middle"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
              loading={isLoading}
            >
              Update
            </Button>
          </Space>
        </Form>
      ) : (
        ""
      )}
    </div>
  );
}
